import { useUser } from './useUser';
import versionInfo from '../components/UserLicense/versions/latest.json';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { contextForPath } from '../utils/usageAnalytics';
import { useRecordUsageActivityMutation } from '../services/projects/usageAnalyticsApi';

const GA_MEASUREMENT_ID = import.meta.env.VITE_APP_GOOGLE_ANALYTICS_TAG;
let trackingAllowed = false;
let trackingContext: Record<string, string | number> = {};
let dashboardAccount: number | undefined;
let lastPageview = '';

// Some dashboard views change customer without changing the URL.
export const setUsageDashboardAccount = (id?: number) => {
  dashboardAccount = id;
  window.dispatchEvent(new Event('usage-context-change'));
};
export const sendPageview = (path: string) => {
  if (!trackingAllowed || !window.gtag) return;
  window.gtag('set', { page_location: window.location.origin + path, page_referrer: window.location.origin });
  window.gtag('event', 'page_view', {
    ...trackingContext, page_location: window.location.origin + path,
    page_path: path, page_title: document.title,
  });
};
export const sendGAEvent = (name: string, params?: Record<string, unknown>) => {
  if (!trackingAllowed || !window.gtag) return;
  const safeName = name.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 40);
  window.gtag('event', safeName, { ...trackingContext, ...params });
};

const useGoogleAnalytics = () => {
  const { userDetails, auth, settings, isAuthenticated } = useUser();
  const location = useLocation();
  const [recordActivity] = useRecordUsageActivityMutation();
  const session = useRef<{ userId: number; id: string } | null>(null);
  const allowed = isAuthenticated && settings?.usage_analytics_excluded === false && userDetails?.accepted_terms_version === versionInfo[0].version;
  const userType = settings?.user_type || auth?.user_type || 'Unclassified';
  const affiliation = settings?.usage_affiliation || settings?.analytics_affiliation ||
    (/@(stryten\.com|gnb\.com|alpinepowersystems\.com)$/i.test(auth?.email || '') ? 'internal' : 'external');

  useEffect(() => {
    trackingAllowed = !!allowed;
    if (!allowed) {
      lastPageview = '';
      session.current = null;
      if (window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('config', GA_MEASUREMENT_ID, { user_id: null, send_page_view: false });
        window.gtag('set', 'user_properties', { userType: null, isStrytenEmployee: null, affiliation: null });
      }
      return;
    }
    if (!GA_MEASUREMENT_ID) return;
    if (!window.gtag) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      script.async = true;
      document.head.appendChild(script);
    }
    window.gtag('set', 'user_properties', { userType, isStrytenEmployee: affiliation === 'internal', affiliation });
    window.gtag('config', GA_MEASUREMENT_ID, { user_id: String(auth.id), page_location: window.location.origin + window.location.pathname, page_referrer: window.location.origin, send_page_view: false, debug_mode: import.meta.env.DEV });
  }, [allowed, auth?.id, userType, affiliation]);

  useEffect(() => {
    if (!allowed) return;
    trackingContext = contextForPath(location.pathname);
    const key = `${auth.id}:${location.pathname}`;
    if (lastPageview !== key) {
      // Query strings may contain contact information or reset tokens; don't transmit them.
      sendPageview(location.pathname);
      lastPageview = key;
    }
  }, [allowed, auth?.id, location.pathname]);

  useEffect(() => {
    if (!allowed) return;
    if (session.current?.userId !== auth.id) session.current = { userId: auth.id, id: crypto.randomUUID() };
    const sessionId = session.current.id;
    let lastTick = performance.now();
    let seconds = 0;
    let active = document.visibilityState === 'visible' && document.hasFocus();
    let context = { ...contextForPath(location.pathname), ...(dashboardAccount ? { portal_account_id: dashboardAccount } : {}) };
    const sample = () => {
      const now = performance.now();
      if (active) seconds += Math.min((now - lastTick) / 1000, 15);
      lastTick = now;
    };
    const flush = () => {
      sample();
      if (seconds < 1) return;
      const elapsed = Math.min(60, Math.floor(seconds));
      seconds -= elapsed;
      void recordActivity({ ...context, event_id: crypto.randomUUID(), session_id: sessionId, expected_user_id: auth.id, engagement_seconds: elapsed });
    };
    const visibility = () => { flush(); active = document.visibilityState === 'visible' && document.hasFocus(); };
    const changedContext = () => {
      flush();
      context = { ...contextForPath(location.pathname), ...(dashboardAccount ? { portal_account_id: dashboardAccount } : {}) };
      trackingContext = context;
      sendGAEvent('viewed_customer_context');
    };
    const sampleTimer = window.setInterval(sample, 5000);
    const flushTimer = window.setInterval(flush, 30000);
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('focus', visibility);
    window.addEventListener('blur', visibility);
    window.addEventListener('usage-context-change', changedContext);
    return () => {
      flush();
      window.clearInterval(sampleTimer); window.clearInterval(flushTimer);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('focus', visibility); window.removeEventListener('blur', visibility);
      window.removeEventListener('usage-context-change', changedContext);
    };
  }, [allowed, auth?.id, location.pathname, recordActivity]);
};
export default useGoogleAnalytics;
